import {useMemo, useState} from 'react';
import type {ModuleEnum} from 'src/api/ProactApiClient';
import {emptySplitApi} from 'src/redux/api';
import {useAppDispatch} from 'src/redux/hooks';
import {findModulePermission} from 'src/shared/auth/useHasModulePermission';
import {usePermissions} from 'src/shared/auth/usePermissions';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useAlert} from 'src/shared/hooks/useAlert';
import {useBackNavigation} from 'src/shared/hooks/useBackNavigation';
import ApiCaller from 'src/shared/utils/ApiCaller';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {
  IStateMachine,
  IStateMachineState,
  IStateMachineTransition,
} from 'src/types/StateMachine';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

type TransitionInvalidateTag = Parameters<
  typeof emptySplitApi.util.invalidateTags
>[0][number];

export interface TransitioningResponse {
  /** Whether to continue with the transition */
  continue: boolean;
  /** Optional override for the transition URL */
  overrideTransitionBaseUrl?: string;
  /** The save was queued to the offline outbox: the transition replays on sync,
   * so it must not be posted now. */
  offlineQueued?: boolean;
}

export interface UseStateTransitionsOptions {
  /** The state machine definition */
  stateMachine: IStateMachine | undefined;
  /** Current state ID of the entity */
  stateId: number | undefined;
  /** Base URL for the transition API endpoint */
  transitionBaseUrl: string;
  /** Module for permission checking */
  module: ModuleEnum;
  /** Callback after successful transition (called before post-transition action) */
  onTransitionComplete?: (
    transition: IStateMachineTransition
  ) => void | Promise<unknown>;
  /** Optional object type filter for transitions */
  objectType?: number;
  /** Bypass permission checks (default: false) */
  bypassPermissions?: boolean;
  /**
   * Pre-transition hook - called before executing a transition.
   * Return `{continue: false}` to cancel the transition.
   * Can also override the transition URL.
   */
  onTransitioning?: (
    transition: IStateMachineTransition
  ) => Promise<TransitioningResponse>;
  /** Posts the transition instead of ApiCaller — the seam an offline domain
   * uses to queue it in the outbox. */
  postTransition?: (args: {
    transition: string;
    note: string;
    occurredAt: string;
  }) => Promise<{offlineQueued?: boolean}>;
  /** Called instead of the post-transition path when the save was queued
   * offline. Falls back to `onTransitionComplete`. */
  onOfflineQueued?: (transition: IStateMachineTransition) => void;
  /** Called after transition completes successfully */
  onTransitioned?: (transition: IStateMachineTransition) => void;
  /** Handler for custom-handler action type transitions */
  onCustomHandlerTransition?: (transition: IStateMachineTransition) => void;
  /** URL for form action type transitions */
  transitionFormUrl?: string;
  /**
   * RTK Query tags to invalidate after a successful transition.
   * Transition POST bypasses RTK Query, so any cached queries that should
   * reflect the new state must be invalidated explicitly here.
   */
  invalidateTagsOnComplete?: TransitionInvalidateTag[];
  /** Back fallback for `redirect-previous` when there is no origin; defaults to the dashboard. */
  fallbackTo?: string;
  /** Replaces navigation for redirect-previous/redirect-home transitions, e.g. so a drawer closes instead. */
  onRedirect?: (transition: IStateMachineTransition) => void;
}

export interface UseStateTransitionsResult {
  /** Available transitions for the current state */
  availableTransitions: IStateMachineTransition[];
  /** Current state info from the state machine */
  currentState: IStateMachineState | undefined;
  /** Whether a transition is in progress */
  isTransitioning: boolean;
  /**
   * Execute a transition.
   * Handles different action types: 'transition', 'form', 'custom-handler'
   */
  executeTransition: (
    transition: IStateMachineTransition,
    note?: string
  ) => Promise<void>;
}

/**
 * Hook to manage state machine transitions.
 *
 * Extracts transition logic into a reusable hook that can be used by
 * both FormTransitionBar and sidebar transition components.
 *
 * Supports:
 * - Permission-based filtering of transitions
 * - Object type filtering
 * - Pre/post transition callbacks
 * - Multiple action types (transition, form, custom-handler)
 * - Post-transition actions (refresh, redirect-previous, redirect-home)
 *
 * @example
 * ```tsx
 * const {availableTransitions, isTransitioning, executeTransition} = useStateTransitions({
 *   stateMachine: machine,
 *   stateId: item?.state,
 *   transitionBaseUrl: `${ApiEndpoints.transitionSafetyEvent}/${id}/transition`,
 *   module: ModuleEnum.SafetyEvent,
 *   onTransitionComplete: () => refetchData(),
 * });
 * ```
 */
export function useStateTransitions({
  stateMachine,
  stateId,
  transitionBaseUrl,
  module,
  onTransitionComplete,
  objectType,
  bypassPermissions = false,
  onTransitioning,
  postTransition,
  onOfflineQueued,
  onTransitioned,
  onCustomHandlerTransition,
  transitionFormUrl,
  invalidateTagsOnComplete,
  fallbackTo = PathUtility.Dashboard(),
  onRedirect,
}: UseStateTransitionsOptions): UseStateTransitionsResult {
  const {setAlert} = useAlert();
  const {permissions} = usePermissions();
  const navigate = useAppNavigate();
  const {goBack, returnTo} = useBackNavigation(fallbackTo);
  const dispatch = useAppDispatch();
  const [isTransitioning, setIsTransitioning] = useState(false);

  const currentState = useMemo(
    () => stateMachine?.states.find(s => s.id === stateId),
    [stateMachine, stateId]
  );

  const availableTransitions = useMemo(() => {
    if (!currentState || !stateMachine) return [];

    let transitions = stateMachine.transitions.filter(
      transition =>
        // Only show visible transitions
        transition.isVisible === true &&
        // Only transitions valid for current state
        transition.from.includes(currentState.id) &&
        // Check permissions (unless bypassed)
        (bypassPermissions ||
          !transition.permissionOperation ||
          findModulePermission(
            permissions,
            module,
            transition.permissionOperation,
            transition.permissionOperationName
          ))
    );

    // Filter by object type if specified
    if (objectType !== undefined) {
      transitions = transitions.filter(
        transition =>
          transition.types !== undefined &&
          transition.types.length > 0 &&
          transition.types.includes(objectType)
      );
    }

    return transitions;
  }, [
    currentState,
    stateMachine,
    permissions,
    module,
    objectType,
    bypassPermissions,
  ]);

  const handlePostTransitionAction = (transition: IStateMachineTransition) => {
    switch (transition.postTransitionAction) {
      case 'refresh':
        void onTransitionComplete?.(transition);
        break;
      case 'redirect-previous':
        if (onRedirect) onRedirect(transition);
        else goBack();
        break;
      case 'redirect-home':
        if (onRedirect) onRedirect(transition);
        else returnTo(PathUtility.Dashboard());
        break;
      default:
        // Default behavior - call onTransitionComplete
        void onTransitionComplete?.(transition);
        break;
    }
  };

  // Sends the transition through the offline seam when one is supplied, else
  // straight to the API; true when the write was queued for a later sync.
  const postOrCall = async (
    transition: IStateMachineTransition,
    note: string,
    url: string
  ): Promise<boolean> => {
    if (postTransition) {
      const {offlineQueued} = await postTransition({
        transition: transition.name,
        note,
        occurredAt: new Date().toISOString(),
      });
      return offlineQueued === true;
    }
    await ApiCaller({
      method: 'POST',
      endpointURL: url,
      data: {transition: transition.name, note},
    });
    return false;
  };

  const runTransition = async (
    transition: IStateMachineTransition,
    note: string,
    url: string
  ): Promise<void> => {
    if (await postOrCall(transition, note, url)) {
      // Queued: the outbox replays it, so nothing server-side changed yet.
      if (onOfflineQueued) {
        onOfflineQueued(transition);
      } else {
        handlePostTransitionAction(transition);
      }
      return;
    }
    if (invalidateTagsOnComplete && invalidateTagsOnComplete.length > 0) {
      dispatch(emptySplitApi.util.invalidateTags(invalidateTagsOnComplete));
    }
    handlePostTransitionAction(transition);
  };

  const executeTransition = async (
    transition: IStateMachineTransition,
    note?: string
  ) => {
    setIsTransitioning(true);

    try {
      // Call pre-transition hook if provided
      let transitioningResponse: TransitioningResponse = {continue: true};
      if (onTransitioning) {
        transitioningResponse = await onTransitioning(transition);
        // Queued offline: the create carries the transition, so replay it on
        // sync instead of posting it against an id that does not exist yet.
        if (transitioningResponse.offlineQueued) {
          if (onOfflineQueued) {
            onOfflineQueued(transition);
          } else {
            handlePostTransitionAction(transition);
          }
          onTransitioned?.(transition);
          setIsTransitioning(false);
          return;
        }
        if (!transitioningResponse.continue) {
          setIsTransitioning(false);
          return;
        }
      }

      // Handle different action types
      switch (transition.action) {
        case 'transition': {
          const url =
            transitioningResponse.overrideTransitionBaseUrl ??
            transitionBaseUrl;

          await runTransition(transition, note ?? '', url);
          break;
        }

        case 'form': {
          if (!transitionFormUrl) {
            throw new Error('Form URL must be supplied for Form transitions.');
          }
          navigate(transitionFormUrl);
          break;
        }

        case 'custom-handler': {
          if (!onCustomHandlerTransition) {
            throw new Error(
              'onCustomHandlerTransition must be supplied for Custom Handler transitions.'
            );
          }
          onCustomHandlerTransition(transition);
          setIsTransitioning(false);
          return; // Early return - custom handler manages its own completion
        }

        default:
          // Default to standard transition behavior
          await runTransition(transition, note ?? '', transitionBaseUrl);
          break;
      }

      // Call post-transition callback
      onTransitioned?.(transition);
    } catch (error) {
      setAlert(parseErrorMessage(error), 'error');
    }

    setIsTransitioning(false);
  };

  return {
    availableTransitions,
    currentState,
    isTransitioning,
    executeTransition,
  };
}
